function normalizeEnvironment(value) {
  return value === 'production' ? 'production' : 'staging';
}

function normalizeBaseUrl(value) {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().replace(/\/$/, '');
}

export function getDspEnvConfig() {
  const environment = normalizeEnvironment(process.env.REACT_APP_DSP_ENVIRONMENT);
  const envBaseUrl =
    environment === 'production'
      ? process.env.REACT_APP_DSP_BASE_URL_PROD
      : process.env.REACT_APP_DSP_BASE_URL_STAGING;
  // No hardcoded fallback domain: an unset base URL stays empty so callers can
  // fail loudly (see getDspBaseUrl) instead of silently using the wrong host.
  const baseUrl = normalizeBaseUrl(
    envBaseUrl ||
      process.env.REACT_APP_DSP_BASE_URL ||
      process.env.REACT_APP_SPOTLIGHT_API_URL ||
      '',
  );

  const envApiKey =
    environment === 'production'
      ? process.env.REACT_APP_DSP_API_KEY_PROD
      : process.env.REACT_APP_DSP_API_KEY_STAGING;
  const apiKey = (envApiKey || process.env.REACT_APP_DSP_API_KEY || '').trim();

  return {
    environment,
    baseUrl,
    apiKey,
  };
}

/**
 * Resolve the DSP/Spotlight base URL used for direct (api-key) DSP requests.
 *
 * These calls (appload, movies, series, search, …) are public and must go
 * straight to myspotlight using the configured env var. We intentionally ignore
 * the platform-injected `window.API_BASE_URL` (the same-origin `/api` proxy):
 * that proxy enforces session auth and 401s public api-key calls. User-data
 * (auth-required) DSP calls also use this base URL — they go direct to
 * myspotlight with the in-memory client token (`x-client-token`), which is
 * re-hydrated from the bkfc_session cookie via `/auth/token`.
 *
 * There is intentionally no hardcoded fallback domain. A missing base URL is a
 * configuration error that must surface loudly rather than silently sending
 * requests to the wrong host.
 *
 * @returns {string} The configured base URL, normalized without a trailing slash.
 * @throws {Error} If no base URL is configured.
 */
export function getDspBaseUrl() {
  const { baseUrl } = getDspEnvConfig();
  if (!baseUrl) {
    throw new Error(
      'DSP base URL is not configured. Set REACT_APP_DSP_BASE_URL_STAGING or ' +
        'REACT_APP_DSP_BASE_URL_PROD (matching REACT_APP_DSP_ENVIRONMENT), or ' +
        'REACT_APP_DSP_BASE_URL / REACT_APP_SPOTLIGHT_API_URL.',
    );
  }
  return baseUrl;
}
